import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { useAuth, API } from './AuthContext';
import { SetupStatusLoader }       from './SetupStatusLoader';
import { SetupAlreadyConfigured }  from './SetupAlreadyConfigured';
import { SetupPasswordForm }       from './SetupPasswordForm';
import { SecurityDateTime }        from '../security/SecurityIntegration.jsx';
import '../security/SecurityStyles.css';

/**
 * SetupPage — First-time administrator password setup.
 *
 * Flow:
 *   1. Check /api/auth/status  →  if isSetup, show "already configured" screen.
 *   2. Show the password form.
 *   3. On success, auto-login and redirect to dashboard.
 */
export default function SetupPage({ onSwitchToLogin }) {
  const { login, checkSetup } = useAuth();
  const [statusChecking, setStatusChecking] = useState(true);
  const [alreadySetup,   setAlreadySetup]   = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');

  // Pre-flight: detect "already configured"
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await fetch(`${API}/auth/status`, { cache: 'no-store' });
        const data = await res.json();
        if (!cancelled) setAlreadySetup(!!data.isSetup);
      } catch (e) {
        if (!cancelled)
          setError(`Cannot reach server (${e?.message || 'network error'}). Check your internet connection.`);
      } finally {
        if (!cancelled) setStatusChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Called by SetupPasswordForm on submit
  const handleSetup = async (password, confirm) => {
    if (password !== confirm) { setError('Passwords do not match.'); return false; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return false; }
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, confirmPassword: confirm }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) {}
      if (res.ok && data.success) {
        setTimeout(() => login(data.token, { username: data.username, email: data.email, isSuperAdmin: data.isSuperAdmin }), 1500);
        return true;          // signal success to SetupPasswordForm
      } else if (res.status === 409 || data.error === 'ALREADY_SETUP') {
        setAlreadySetup(true);
        return false;
      } else {
        setError(data.message || `Setup failed (HTTP ${res.status}).`);
        return false;
      }
    } catch (err) {
      setError(`Network error: ${err?.message || 'Failed to fetch'}. Check your connection.`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  if (statusChecking) return <SetupStatusLoader />;
  if (alreadySetup)   return <SetupAlreadyConfigured onSwitchToLogin={onSwitchToLogin} checkSetup={checkSetup} />;

  return (
    <div className="auth-page">
      <div style={{ position: 'absolute', top: 20, right: 28 }}>
        <SecurityDateTime style={{ color: 'var(--auth-text-muted)' }} />
      </div>
      <div className="auth-page-grid" />
      <div className="auth-shell">
        <div className="auth-logo-wrap">
          <div className="auth-logo-badge" style={{ background: '#10b981', boxShadow: '0 0 32px rgba(16,185,129,0.4)' }}>
            <Shield size={26} color="#fff" />
          </div>
          <h1 className="auth-title">First-Time Setup</h1>
          <p className="auth-subtitle">Set Your Administrator Password</p>
        </div>
        <div className="auth-card">
          <p style={{ color: 'var(--auth-text-muted)', fontSize: 12, lineHeight: 1.7, marginBottom: 24 }}>
            Welcome,{' '}
            <span style={{ color: 'var(--brand-blue)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
              HVSNKCVNM
            </span>
            . Create your secure administrator password below. This will be stored with bcrypt-12 hashing.
          </p>
          <SetupPasswordForm onSubmit={handleSetup} error={error} loading={loading} />
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
